import { Box, Link as ChakraLink, Flex, Grid, Image, Text } from '@chakra-ui/react';

import { Link as RouterLink } from '#features/routing';
import { Link } from '#features/ui';
import {
  BOT_INVITE_URL,
  DISCORD_BOT_LIST_URL,
  SUPPORT_DISCORD_URL,
  SUPPORT_EMAIL,
  TOP_GG_VOTE_URL,
} from '../../data';

const LFG_LOGO_SRC = 'https://media.teamplay.gg/assets/E7ZD/sOE.png';

const linkStyle = {
  textStyle: 'sm',
  color: 'fg.muted',
  _hover: { color: 'fg', textDecoration: 'none' },
} as const;

const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <ChakraLink href={href} target="_blank" rel="noopener noreferrer" {...linkStyle}>
    {children}
  </ChakraLink>
);

/**
 * Dedicated landing footer (the page sets `rendersOwnFooter`): bot-specific links
 * (invite, dashboard, support, votes, the App legal docs) in the main landing
 * footer's visual language instead of the consumer teamplay.gg footer.
 */
export const BotFooter = () => (
  <Box as="footer" w="100%" pt={{ base: 10, md: 12 }} pb={8}>
    <Box
      w="100%"
      h="1px"
      mb={{ base: 8, md: 10 }}
      background="linear-gradient(90deg, transparent 0%, #5865F24D 30%, #5865F24D 70%, transparent 100%)"
    />

    <Grid
      templateColumns={{ base: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1fr 1fr' }}
      gap={{ base: 8, lg: 10 }}
      mb={{ base: 8, md: 10 }}
    >
      <Flex direction="column" gap={3} alignItems="flex-start">
        <RouterLink to="/{-$locale}/discord-lfg-bot">
          <Image src={LFG_LOGO_SRC} alt="Teamplay LFG" h="32px" w="auto" />
        </RouterLink>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.7" maxW="260px">
          The Discord LFG bot by Teamplay.gg – structured looking-for-group posts, sessions and
          voice for gaming communities.
        </Text>
      </Flex>

      <Flex direction="column" gap={2}>
        <Text fontWeight="700" textStyle="sm" mb={1} color="fg.emphasized">
          Bot
        </Text>
        <ExternalLink href={BOT_INVITE_URL}>Add to Discord</ExternalLink>
        <Link to="/{-$locale}/discord-lfg-bot/admin" {...linkStyle}>
          Web dashboard
        </Link>
        <ExternalLink href={SUPPORT_DISCORD_URL}>Support server</ExternalLink>
        <ChakraLink href={`mailto:${SUPPORT_EMAIL}`} {...linkStyle}>
          Contact us
        </ChakraLink>
      </Flex>

      <Flex direction="column" gap={2}>
        <Text fontWeight="700" textStyle="sm" mb={1} color="fg.emphasized">
          Support us
        </Text>
        <ExternalLink href={TOP_GG_VOTE_URL}>Vote on Top.gg</ExternalLink>
        <ExternalLink href={DISCORD_BOT_LIST_URL}>Vote on Discord Bot List</ExternalLink>
      </Flex>

      <Flex direction="column" gap={2}>
        <Text fontWeight="700" textStyle="sm" mb={1} color="fg.emphasized">
          Teamplay.gg
        </Text>
        <Link to="/{-$locale}" {...linkStyle}>
          Find teammates
        </Link>
        <Link to="/{-$locale}/blog" {...linkStyle}>
          Blog
        </Link>
        <Link to="/{-$locale}/faq" {...linkStyle}>
          FAQ
        </Link>
      </Flex>
    </Grid>

    <Flex
      pt={6}
      borderTopWidth="1px"
      borderColor="whiteAlpha.100"
      direction={{ base: 'column', sm: 'row' }}
      align="center"
      justify="space-between"
      gap={3}
    >
      <Text textStyle="xs" color="fg.muted">
        © {new Date().getFullYear()} Teamplay.gg
      </Text>
      <Flex gap={4} align="center" wrap="wrap" justify="center">
        <Link to="/{-$locale}/discord-lfg-app/privacy-policy" {...linkStyle} textStyle="xs">
          Privacy Policy
        </Link>
        <Link to="/{-$locale}/discord-lfg-app/terms-and-conditions" {...linkStyle} textStyle="xs">
          Terms of Use
        </Link>
      </Flex>
    </Flex>
  </Box>
);
